/* ============================================================================
   FRONT LINE DEV - DO NOT EDIT
   This file powers Infinit's custom Quiz and Custom Formula functionality and
   is maintained exclusively by Front Line Dev. Editing it may break the
   formula builder, quiz, saved formulas, or related features. Please contact
   Front Line Dev for any changes.
   ============================================================================ */

/**
 * My Quizzes — read-only history of the customer's quiz submissions.
 *
 * Each row links to /products/quiz-results?submission=<token>, which
 * re-hydrates the results page from the stored submission (works
 * across devices / browser sessions because the token is canonical).
 *
 * No edit / no delete by design — this is an audit record of the
 * answers the customer gave at submission time. To re-take, the
 * customer goes to /pages/quiz again.
 */
(() => {
  const root = document.getElementById('my-quizzes-app');
  if (!root) return;
  const base = root.dataset.proxyBase || '/apps/formulas';

  // ─── Question dictionary (kept in sync with snippets/quiz-step-*.liquid) ───
  // Used to humanize older submissions that don't include answers_display.
  // For radio/checkbox questions: { question, options: { code: label } }.
  // For slider questions:         { question, slider: ['lowLabel', ..., 'highLabel'] }
  // For free text/number/date:    { question } only.
  const QUESTION_DEFS = {
    q1_sport_focus:                { question: 'What are you training for?',                options: { triathlon: 'Triathlon', cycling: 'Cycling', running: 'Running', swimming: 'Swimming', strength: 'Strength/HIIT', paddling: 'Paddling', other: 'Other' } },
    q1_triathlon_type:             { question: 'Triathlon — which event(s)?',               options: { sprint: 'Sprint', olympic: 'Olympic', ironman: '70.3 or Ironman' } },
    q1_cycling_type:               { question: 'Cycling — which discipline(s)?',            options: { gravel: 'Gravel', road: 'Road', mtb: 'MTB', time_trial: 'Time Trial', cyclocross: 'Cyclocross' } },
    q1_running_type:               { question: 'Running — which distance(s)?',              options: { '5k_10k': '5k/10k', half_marathon: 'Half marathon', marathon: 'Marathon', ultra_marathon: 'Ultra-marathon' } },
    q1_swimming_type:              { question: 'Swimming — which kind?',                    options: { pool: 'Pool', open_water: 'Open Water' } },
    q1_strength_type:              { question: 'Strength/HIIT — which kind?',               options: { crossfit: 'Crossfit', weight_lifting: 'Weight lifting', hyrox: 'HYROX', other: 'Other' } },
    q1_paddling_type:              { question: 'Paddling — which kind?',                    options: { kayaking: 'Kayaking', canoe: 'Canoe', paddleboard: 'Paddleboard', other: 'Other' } },
    q1_triathlon_primary_event:    { question: 'Triathlon — primary event' },
    q1_cycling_primary_event:      { question: 'Cycling — primary event' },
    q1_running_primary_event:      { question: 'Running — primary event' },
    q1_swimming_primary_event:     { question: 'Swimming — primary event' },
    q1_strength_primary_event:     { question: 'Strength/HIIT — primary event' },
    q1_paddling_primary_event:     { question: 'Paddling — primary event' },
    q2_workout_length:             { question: "What's your longest workout?",              options: { '1_hour': '1 hour', '2_hours': '2 hours', '3_plus_hours': '3+ hours' } },
    q2_hunger:                     { question: "How's your hunger?",                        slider: ['Not hungry at all', 'Slightly hungry', 'Pretty hungry', 'Starving'] },
    q3_fueling_habits:             { question: 'What are your fueling habits?',             options: { preworkout: 'Preworkout', gels: 'Gels', chews: 'Chews', bars: 'Bars', salt_pills: 'Salt Pills', real_food: 'Real food', drink_mix: 'Drink Mix', none: 'None of the above' } },
    q4_athlete_level:              { question: 'How would you describe yourself?',          slider: ['DFL', 'Recreational', 'Middle of the Pack', 'Competitive', 'Elite'] },
    q5_sweat_level:                { question: 'How sweaty are you?',                       slider: ['Light Glow', 'Solid Drip', 'Wringing Wet'] },
    q6_sweat_rate_data:            { question: 'Do you have sweat rate data?',              options: { yes: 'Yes', no: 'No' } },
    q7_sweat_saltiness:            { question: 'How salty is your sweat?',                  slider: ['Just a Sprinkle', 'Pretty Salty', 'Noticeably Salty'] },
    q8_cramp_frequency:            { question: 'How often do you cramp?',                   slider: ['Never', 'Rarely', 'Sometimes', 'Often', 'All the Time'] },
    q9_birth_date:                 { question: "What's your DOB?" },
    q10_weight_kg:                 { question: 'How much do you weigh? (kg)' },
    q10_weight_lbs:                { question: 'How much do you weigh? (lbs)' }, // submissions before the kg change
    q11_sex:                       { question: "What's your sex?",                          options: { male: 'Male', female: 'Female', prefer_not: 'Prefer Not to Say' } },
    q12_body_fat_category:         { question: 'Do you know your % body fat?',              options: { dont_know: "I don't know", lt10: '<10%', '10_15': '10–15%', '15_25': '15–25%', gt25: 'over 25%', lt15: '<15%', '25_35': '25–35%', gt35: 'over 35%', exact: 'I know my EXACT %' } },
    q13_body_fat_exact:            { question: 'What is your % body fat?' },
    q14_body_type_vibe:            { question: 'What is your body type?',                   options: { chiseled: 'Chiseled', lean: 'Lean', average: 'Average', could_lose: 'Want to lose a few kilos' } },
    q15_region:                    { question: 'Where do you live?',                        options: { qld: 'Queensland', nsw: 'New South Wales', act: 'Australian Capital Territory', vic: 'Victoria', tas: 'Tasmania', sa: 'South Australia', wa_north: 'Northern Western Australia', wa_south: 'Southern Western Australia', nt: 'Northern Territory', nz: 'New Zealand', southeast: 'Queensland, Northern Territory & Northern Western Australia', midwest: 'New South Wales, ACT, South Australia & Southern Western Australia', northeast: 'Victoria, Tasmania & New Zealand', southwest: 'Southwest', mountainwest: 'Mountain West', northwest: 'Northwest', pacific_islands: 'Pacific Islands', caribbean: 'Caribbean' } },
    q16_workout_location:          { question: 'Where do you workout?',                     options: { inside: 'Inside', outside: 'Outside', both: 'Both' } },
    q17_flavor_strength:           { question: 'How strong do you like your flavor?',       slider: ['Just a hint', 'Strong'] },
    q18_flavor_vibe:               { question: 'Which type of flavor do you most enjoy?',   options: { bright_citrusy: 'Subtly Citrus', fruit_berry: 'Bright & Berry', creamy_cozy: 'Creamy & Cozy', unflavored: 'Unflavored' } },
    q19_caffeine_pref:             { question: 'Do you currently train with caffeine?',     options: { none: 'Caffeine-free', moderate: 'Light Buzz Only', more: 'Little More Buzz', decent: 'Decent Buzz', high: 'Fully Caffeinated', unknown: 'Not Sure' } },
    q20_dietary_prefs:             { question: 'Do you have any dietary restrictions or preferences?', options: { dairy_free: 'Dairy free', soy_free: 'Soy Free', gluten_free: 'Gluten Free', plant_based: 'Plant Based Diet', vegan: 'Strictly Vegan', none: 'None' } },
    q21_protein_powder:            { question: 'Do you currently use protein powder?',      options: { yes: 'Yes', no: 'No' } },
    q22_protein_use_cases:         { question: 'How does protein fit into your routine?',   options: { meal_replacement: 'Meal replacement', post_workout: 'Post-workout', snack: 'Snack', before_bed: 'Before Bed', wellness: 'Wellness', other: 'Other' } },
    q24_game_plan:                 { question: "What's the game plan?",                     options: { lose: 'Lose weight', gain: 'Gain weight', maintain: 'Maintain weight' } },
    q25_fruits_veggies:            { question: 'How many servings of fruits and veggies do you get per day?', slider: ['0', '1', '2', '3', '4', '5+'] },
    q26_soreness:                  { question: 'How sore are you after workouts?',          slider: ['Not at all', 'A little', 'Totally wrecked'] },
    q27_workout_time:              { question: 'What time of day do you usually workout?',  options: { morning: 'Morning', afternoon: 'Afternoon', night: 'Night' } },
  };

  function humanizeQuestion(key) {
    // Strip the trailing [] from multi-select names (q3_fueling_habits[] etc.)
    const baseKey = key.replace(/\[\]$/, '');
    const def = QUESTION_DEFS[baseKey];
    return def ? def.question : baseKey;
  }

  function humanizeAnswer(key, value) {
    const baseKey = key.replace(/\[\]$/, '');
    const def = QUESTION_DEFS[baseKey];
    if (!def) return Array.isArray(value) ? value.join(', ') : String(value);

    // Multi-select: array of option codes
    if (Array.isArray(value)) {
      return value.map((v) => (def.options && def.options[v]) || v).join(', ');
    }

    // Radio with option map
    if (def.options && def.options[value] != null) {
      return def.options[value];
    }

    // Slider: map numeric 0-100 to nearest label by position
    if (def.slider && def.slider.length && /^\d+(\.\d+)?$/.test(String(value))) {
      const n = Math.max(0, Math.min(100, parseFloat(value)));
      const bucket = Math.round((n / 100) * (def.slider.length - 1));
      return `${def.slider[bucket]} (${Math.round(n)}/100)`;
    }

    return String(value);
  }

  const state = {
    page: 1,
    pageSize: 20,
    items: [],
    total: 0,
    loading: false,
    error: '',
  };

  function el(tag, attrs, children) {
    const n = document.createElement(tag);
    if (attrs) {
      Object.entries(attrs).forEach(([k, v]) => {
        if (k === 'class') n.className = v;
        else if (k === 'html') n.innerHTML = v;
        else if (k.startsWith('on') && typeof v === 'function') n.addEventListener(k.slice(2), v);
        else if (k === 'style' && typeof v === 'object') Object.assign(n.style, v);
        else n.setAttribute(k, v);
      });
    }
    if (children) {
      (Array.isArray(children) ? children : [children]).forEach((c) => {
        if (c == null) return;
        n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
      });
    }
    return n;
  }

  function fmtDate(str) {
    if (!str) return '';
    const d = new Date(str.replace(' ', 'T') + (str.includes('Z') ? '' : 'Z'));
    if (isNaN(d)) return str;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      + ' · '
      + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  function picksLabel(summary) {
    const picks = (summary && summary.picks) || {};
    const names = ['hydrationA', 'hydrationB', 'protein']
      .map((k) => picks[k] && picks[k].name)
      .filter(Boolean);
    if (!names.length) return 'Custom plan';
    return names.join(' · ');
  }

  async function fetchList() {
    state.loading = true;
    state.error = '';
    render();
    try {
      const url = `${base}/quiz/list?page=${state.page}&pageSize=${state.pageSize}`;
      const r = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.ok) throw new Error(data.message || `Request failed (${r.status})`);
      state.items = data.items || [];
      state.total = data.total || 0;
    } catch (e) {
      state.error = e.message || 'Failed to load quiz history';
    } finally {
      state.loading = false;
      render();
    }
  }

  function styles() {
    return `
      .mq-card { font-family: Inter,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif; }
      .mq-row { border:1px solid #e5e5e5; border-radius:10px; padding:16px 20px; margin-bottom:12px; display:flex; align-items:center; gap:16px; flex-wrap:wrap; background:#fff; }
      .mq-meta { font-size:13px; color:#666; }
      .mq-title { font-size:16px; font-weight:600; color:#111; margin:0 0 4px; }
      .mq-actions { margin-left:auto; display:flex; gap:8px; flex-wrap:wrap; }
      .mq-btn { display:inline-block; padding:8px 16px; border-radius:8px; background:#111; color:#fff; font-size:14px; font-weight:500; text-decoration:none; border:1px solid #111; cursor:pointer; font-family:inherit; }
      .mq-btn:hover { opacity:.9; }
      .mq-btn--ghost { background:#fff; color:#111; }
      .mq-empty, .mq-err, .mq-loading { font-size:14px; color:#666; padding:24px; text-align:center; border:1px dashed #ddd; border-radius:10px; }
      .mq-err { color:#a00; border-color:#fbb; background:#fff5f5; }
      .mq-pager { display:flex; gap:8px; align-items:center; justify-content:center; margin-top:16px; font-size:13px; color:#666; }
      .mq-pager button { border:1px solid #ddd; background:#fff; padding:6px 12px; border-radius:6px; cursor:pointer; }
      .mq-pager button:disabled { opacity:.4; cursor:not-allowed; }

      .mq-modal-backdrop {
        position:fixed; inset:0; background:rgba(15,15,15,.55);
        backdrop-filter:blur(8px); -webkit-backdrop-filter:blur(8px);
        z-index:9999; display:flex; align-items:center; justify-content:center;
        padding:24px;
        animation: mq-fade-in .15s ease-out;
      }
      @keyframes mq-fade-in { from { opacity:0; } to { opacity:1; } }
      .mq-modal {
        background:#fff; border-radius:12px;
        max-width:640px; width:100%; max-height:85vh;
        display:flex; flex-direction:column;
        box-shadow:0 20px 60px rgba(0,0,0,.25);
        font-family: Inter,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
      }
      .mq-modal__header {
        padding:20px 24px; border-bottom:1px solid #eee;
        display:flex; align-items:center; gap:12px;
      }
      .mq-modal__title { margin:0; font-size:18px; font-weight:600; color:#111; flex:1; }
      .mq-modal__close {
        background:none; border:none; font-size:24px; line-height:1; cursor:pointer; color:#666;
        padding:4px 10px; border-radius:6px;
      }
      .mq-modal__close:hover { background:#f4f4f4; color:#111; }
      .mq-modal__body {
        padding:8px 24px 24px; overflow-y:auto; flex:1;
      }
      .mq-qa { padding:14px 0; border-bottom:1px solid #f0f0f0; }
      .mq-qa:last-child { border-bottom:none; }
      .mq-qa__q { font-size:13px; color:#666; margin:0 0 4px; font-weight:500; }
      .mq-qa__a { font-size:15px; color:#111; margin:0; line-height:1.4; }
      .mq-modal__loading, .mq-modal__err {
        padding:32px 24px; text-align:center; color:#666; font-size:14px;
      }
      .mq-modal__err { color:#a00; }
    `;
  }

  // ─── Answers modal ───

  function openAnswersModal(item) {
    const backdrop = el('div', { class: 'mq-modal-backdrop', role: 'dialog', 'aria-modal': 'true' });
    const modal = el('div', { class: 'mq-modal' });
    const header = el('div', { class: 'mq-modal__header' }, [
      el('h2', { class: 'mq-modal__title' }, 'Your Answers'),
      el('button', {
        class: 'mq-modal__close',
        type: 'button',
        'aria-label': 'Close',
        onclick: () => close(),
      }, '×'),
    ]);
    const body = el('div', { class: 'mq-modal__body' });
    body.appendChild(el('div', { class: 'mq-modal__loading' }, 'Loading your answers…'));
    modal.appendChild(header);
    modal.appendChild(body);
    backdrop.appendChild(modal);

    function close() {
      backdrop.remove();
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    }
    function onKey(e) { if (e.key === 'Escape') close(); }
    backdrop.addEventListener('click', (e) => { if (e.target === backdrop) close(); });
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    document.body.appendChild(backdrop);

    const url = `${base}/quiz/get?submission=${encodeURIComponent(item.submission_token)}`;
    fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((json) => {
        if (!json || !json.ok || !json.submission || !json.submission.payload) {
          throw new Error('Submission details unavailable');
        }
        renderAnswers(body, json.submission.payload);
      })
      .catch((e) => {
        body.innerHTML = '';
        body.appendChild(el('div', { class: 'mq-modal__err' }, e.message || 'Failed to load answers'));
      });
  }

  function renderAnswers(body, payload) {
    body.innerHTML = '';
    const display = payload && payload.answers_display;

    if (Array.isArray(display) && display.length) {
      display.forEach((qa) => {
        const block = el('div', { class: 'mq-qa' }, [
          el('p', { class: 'mq-qa__q' }, qa.question || ''),
          el('p', { class: 'mq-qa__a' }, qa.answer || ''),
        ]);
        body.appendChild(block);
      });
      return;
    }

    // Fallback for older submissions that predate the answers_display
    // capture in quiz.js — translate input names to human questions using
    // QUESTION_DEFS, and translate values to their labels.
    const raw = (payload && payload.answers) || {};
    const keys = Object.keys(raw).filter((k) => k.indexOf('customer[') !== 0 && k !== 'quiz_consent_plan');
    if (!keys.length) {
      body.appendChild(el('div', { class: 'mq-modal__err' }, 'No answer details recorded for this submission.'));
      return;
    }

    // Order by the numeric prefix of qN_... so the modal reads in panel order.
    keys.sort((a, b) => {
      const ma = /^q(\d+)/.exec(a);
      const mb = /^q(\d+)/.exec(b);
      if (ma && mb) return parseInt(ma[1], 10) - parseInt(mb[1], 10);
      return a.localeCompare(b);
    });

    keys.forEach((k) => {
      const v = raw[k];
      const block = el('div', { class: 'mq-qa' }, [
        el('p', { class: 'mq-qa__q' }, humanizeQuestion(k)),
        el('p', { class: 'mq-qa__a' }, humanizeAnswer(k, v)),
      ]);
      body.appendChild(block);
    });
  }

  function render() {
    root.innerHTML = '';
    root.appendChild(el('style', { html: styles() }));

    const card = el('div', { class: 'mq-card' });

    if (state.loading) {
      card.appendChild(el('div', { class: 'mq-loading' }, 'Loading your quiz history…'));
    } else if (state.error) {
      card.appendChild(el('div', { class: 'mq-err' }, state.error));
    } else if (!state.items.length) {
      card.appendChild(el('div', { class: 'mq-empty' }, [
        'You haven’t completed a quiz yet. ',
        el('a', { href: '/pages/quiz', style: { color: '#111', textDecoration: 'underline' } }, 'Take the quiz'),
        '.'
      ]));
    } else {
      state.items.forEach((item) => {
        const row = el('div', { class: 'mq-row' });
        const left = el('div', null, [
          el('div', { class: 'mq-title' }, picksLabel(item.summary)),
          el('div', { class: 'mq-meta' }, [
            'Submitted ' + fmtDate(item.created_at),
            (item.summary && item.summary.sport) ? ' · ' + item.summary.sport : '',
            (item.summary && item.summary.goal) ? ' · ' + item.summary.goal : '',
          ].filter(Boolean).join('')),
        ]);
        const actions = el('div', { class: 'mq-actions' }, [
          el('button', {
            class: 'mq-btn mq-btn--ghost',
            type: 'button',
            onclick: () => openAnswersModal(item),
          }, 'View Answers'),
          el('a', {
            class: 'mq-btn',
            href: '/products/quiz-results?submission=' + encodeURIComponent(item.submission_token),
          }, 'View Results'),
        ]);
        row.appendChild(left);
        row.appendChild(actions);
        card.appendChild(row);
      });

      // Pager
      const pages = Math.max(1, Math.ceil(state.total / state.pageSize));
      if (pages > 1) {
        const pager = el('div', { class: 'mq-pager' });
        const prev = el('button', {
          onclick: () => { if (state.page > 1) { state.page--; fetchList(); } }
        }, '‹ Prev');
        prev.disabled = state.page <= 1;
        const next = el('button', {
          onclick: () => { if (state.page < pages) { state.page++; fetchList(); } }
        }, 'Next ›');
        next.disabled = state.page >= pages;
        pager.appendChild(prev);
        pager.appendChild(el('span', null, `Page ${state.page} of ${pages} · ${state.total} total`));
        pager.appendChild(next);
        card.appendChild(pager);
      }
    }

    root.appendChild(card);
  }

  fetchList();
})();
